// src/components/ClosingChecklistsReport.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Dialog, DialogContent, IconButton, CircularProgress, Tooltip, TablePagination,
} from '@mui/material';
import { Sunrise, Moon, NotebookPen, AlertTriangle, X, Minus } from 'lucide-react';
import api from '../services/api';
import OpeningCheckListView from './OpeningCheckListView';
import ClosingChecklistView from './ClosingChecklistView';
import DailyActivityReportView from './DailyActivityReportView';

const todayISO = () => new Date().toISOString().slice(0, 10);

// One accent per checklist type — carried from row pill through to the detail
// view header, so the color itself tells you which record you're looking at.
const TYPES = {
  opening: { label: 'Opening', icon: Sunrise, color: '#2563eb', endpoint: 'admin/opening-checklists' },
  closing: { label: 'Closing', icon: Moon,    color: '#16a34a', endpoint: 'admin/closing-checklists' },
  report:  { label: 'Report',  icon: NotebookPen, color: '#7c3aed', endpoint: 'admin/daily-reports' },
};

function StatusPill({ type, entry, onOpen }) {
  const { icon: Icon, color } = TYPES[type];
  const exists = entry?.exists;

  if (!exists) {
    return (
      <Chip
        size="small"
        icon={<Minus size={13} />}
        label="Missing"
        sx={{
          bgcolor: 'transparent', color: '#9ca3af', border: '1px solid #e0e0e0',
          fontWeight: 600, '& .MuiChip-icon': { color: '#9ca3af', ml: '4px' },
        }}
      />
    );
  }

  // "Incidente" só vale o que a IA marcou (ai_daily_summaries.has_alerts) —
  // o campo de texto `incidents` é preenchido todo dia, mesmo sem nada acontecer.
  let tooltip = 'View details';
  if (type === 'closing' && entry.validated_sales != null) {
    tooltip = `Sales $${entry.validated_sales.toFixed(2)} · Tips $${(entry.validated_tips ?? 0).toFixed(2)}`;
  } else if (type === 'report') {
    tooltip = entry.has_incidents
      ? 'AI flagged this employee\'s report'
      : entry.ai_reviewed ? 'Reviewed by AI — nothing flagged' : 'Not reviewed by AI yet';
  }

  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        onClick={() => onOpen(type, entry.id)}
        icon={<Icon size={13} />}
        label="Signed"
        deleteIcon={entry.has_incidents ? <AlertTriangle size={13} /> : undefined}
        onDelete={entry.has_incidents ? () => onOpen(type, entry.id) : undefined}
        sx={{
          bgcolor: `${color}14`, color, border: `1px solid ${color}40`, fontWeight: 700,
          cursor: 'pointer', '& .MuiChip-icon': { color }, '& .MuiChip-deleteIcon': { color: '#dc2626' },
          '&:hover': { bgcolor: `${color}26` },
        }}
      />
    </Tooltip>
  );
}

function StatTile({ icon: Icon, color, label, done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <Box sx={{
      flex: '1 1 160px', display: 'flex', alignItems: 'center', gap: 1.5,
      p: 1.5, borderRadius: 2, bgcolor: 'background.paper', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
        bgcolor: `${color}14`, color, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={18} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>
          {done}<Typography component="span" sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500 }}>/{total}</Typography>
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {label} · {pct}%
        </Typography>
      </Box>
    </Box>
  );
}

const EMPTY_STATS = { total: 0, opening: 0, closing: 0, report: 0, incidents: 0 };

export default function ClosingChecklistsReport() {
  // Defaults to today only, so opening the page never pulls a whole
  // history's worth of shifts before the manager picks a range.
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());
  const [hotelId, setHotelId] = useState('');
  const [employeeId, setEmployeeId] = useState('');

  const [hotels, setHotels] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [detail, setDetail] = useState(null); // { type, data }
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    api.get('/hotels').then(res => setHotels(res.data)).catch(() => {});
    api.get('/api/employees_simple_list').then(res => setEmployees(res.data)).catch(() => {});
  }, []);

  const fetchRows = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);
    api.get('/admin/checklists-overview', {
      params: {
        start_date: startDate,
        end_date: endDate,
        hotel_id: hotelId || undefined,
        employee_id: employeeId || undefined,
        page,
        page_size: rowsPerPage,
      },
    })
      .then(res => { setRows(res.data.data); setTotal(res.data.total); setStats(res.data.stats); })
      .catch(() => { setRows([]); setTotal(0); setStats(EMPTY_STATS); })
      .finally(() => setLoading(false));
  }, [startDate, endDate, hotelId, employeeId, page, rowsPerPage]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { setPage(0); }, [startDate, endDate, hotelId, employeeId]);

  const openDetail = (type, id) => {
    setDetailLoading(true);
    api.get(`/${TYPES[type].endpoint}/${id}`)
      .then(res => setDetail({ type, data: res.data }))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Checklists</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Opening, closing and daily activity sign-offs, per shift.
        </Typography>
      </Box>

      {/* ── Summary ── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <StatTile icon={Sunrise} color={TYPES.opening.color} label="Openings signed" done={stats.opening} total={stats.total} />
        <StatTile icon={Moon} color={TYPES.closing.color} label="Closings signed" done={stats.closing} total={stats.total} />
        <StatTile icon={NotebookPen} color={TYPES.report.color} label="Reports submitted" done={stats.report} total={stats.total} />
        <Tooltip title="Days where the AI daily summary (Overview tab) found something worth flagging — not just days with text in the report field.">
          <Box sx={{
            flex: '1 1 160px', display: 'flex', alignItems: 'center', gap: 1.5,
            p: 1.5, borderRadius: 2, boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            bgcolor: stats.incidents > 0 ? '#fef2f2' : 'background.paper',
          }}>
            <Box sx={{
              width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
              bgcolor: stats.incidents > 0 ? '#fee2e2' : '#f3f4f6',
              color: stats.incidents > 0 ? '#dc2626' : '#9ca3af',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertTriangle size={18} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2, color: stats.incidents > 0 ? '#dc2626' : 'text.primary' }}>
                {stats.incidents}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Flagged by AI</Typography>
            </Box>
          </Box>
        </Tooltip>
      </Box>

      {/* ── Filters ── */}
      <Box sx={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2,
        p: 2, borderRadius: 2, bgcolor: 'background.paper', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', border: '1.5px solid #d0d0d0', borderRadius: 2, px: 1.5, py: 0.75 }}>
          <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>From</Typography>
          <input type="date" value={startDate} max={endDate} onChange={e => setStartDate(e.target.value)}
            style={{ background: 'transparent', border: 'none', fontSize: '0.875rem', fontWeight: 700, outline: 'none' }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', border: '1.5px solid #d0d0d0', borderRadius: 2, px: 1.5, py: 0.75 }}>
          <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }}>To</Typography>
          <input type="date" value={endDate} min={startDate} onChange={e => setEndDate(e.target.value)}
            style={{ background: 'transparent', border: 'none', fontSize: '0.875rem', fontWeight: 700, outline: 'none' }} />
        </Box>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Hotel</InputLabel>
          <Select label="Hotel" value={hotelId} onChange={e => setHotelId(e.target.value)}>
            <MenuItem value="">All hotels</MenuItem>
            {hotels.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Employee</InputLabel>
          <Select label="Employee" value={employeeId} onChange={e => setEmployeeId(e.target.value)}>
            <MenuItem value="">All employees</MenuItem>
            {employees.map(e => <MenuItem key={e.id} value={e.id}>{e.name}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* ── Table ── */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Hotel</TableCell>
              <TableCell>Employee</TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: TYPES.opening.color }}>
                  <Sunrise size={14} /> Opening
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: TYPES.closing.color }}>
                  <Moon size={14} /> Closing
                </Box>
              </TableCell>
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, color: TYPES.report.color }}>
                  <NotebookPen size={14} /> Report
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>No shifts found for this filter.</TableCell></TableRow>
            )}
            {!loading && rows.map(r => (
              <TableRow key={`${r.employee_id}-${r.hotel_id}-${r.entry_date}`} hover>
                <TableCell>{r.entry_date}</TableCell>
                <TableCell>{r.hotel_name}</TableCell>
                <TableCell>{r.employee_name}</TableCell>
                <TableCell align="center"><StatusPill type="opening" entry={r.opening} onOpen={openDetail} /></TableCell>
                <TableCell align="center"><StatusPill type="closing" entry={r.closing} onOpen={openDetail} /></TableCell>
                <TableCell align="center"><StatusPill type="report" entry={r.report} onOpen={openDetail} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </TableContainer>

      {/* ── Detail modal ── */}
      <Dialog open={!!detail || detailLoading} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogContent sx={{ position: 'relative', p: detail ? 0 : 4 }}>
          <IconButton onClick={() => setDetail(null)} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'white', boxShadow: 1 }} size="small">
            <X size={18} />
          </IconButton>
          {detailLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
          {!detailLoading && detail?.type === 'opening' && <OpeningCheckListView data={detail.data} />}
          {!detailLoading && detail?.type === 'closing' && <ClosingChecklistView closingData={detail.data} />}
          {!detailLoading && detail?.type === 'report' && <DailyActivityReportView reportData={detail.data} signatureSvg={detail.data.signature_svg} />}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
